import {
  Project,
  Identifiable,
  List,
  list,
  Gantt,
  Zone,
  Resource,
  GanttSort,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";
import { LinkedProject, Linker } from "../linkers/types";

type Response = {
  _embedded: {
    projects: (LinkedProject & {
      activityRoot: Identifiable<List>;
      _links: {
        [key: string]: { href: string };
      };
    })[];
  };
};

function projectsToGantt(projects: Identifiable<Project>[]): Gantt {
  return {
    projects: projects.map((project) => ({
      ...project,
      lists: [project.rootList],
      shallow: true,
    })),
    constraints: [],
  };
}

function getGantt(httpClient: HttpClient) {
  return (
    sort: GanttSort,
    page: number,
    size: number,
    zone?: Identifiable<Zone>,
    resources?: Identifiable<Resource>[],
  ) => {
    const zoneFilter = zone ? `zone.uuid==${zone.uuid} and ` : "";
    const resourceFilter =
      resources && resources.length > 0
        ? `(${resources.map((r) => `resources.member.uuid==${r.uuid}`).join(" or ")}) and `
        : "";
    const sortMap: { [Key in GanttSort]: string } = {
      ascendingName: "sort=name,asc&",
      ascendingStartDate: "sort=activityRoot.startDate,asc&",
      ascendingEndDate: "sort=activityRoot.endDate,asc&",
      ascendingCompletionDate: "",
      ascendingCreationDate: "sort=createdDate,asc&",
      ascendingRank: "sort=zone.name,asc&",
      ascendingAuthor: "sort=name,asc&",
      descendingName: "sort=name,desc&",
      descendingStartDate: "sort=activityRoot.startDate,desc&",
      descendingEndDate: "sort=activityRoot.endDate,desc&",
      descendingCompletionDate: "",
      descendingCreationDate: "sort=createdDate,desc&",
      descendingRank: "sort=zone.name,desc&",
      descendingAuthor: "sort=name,desc&",
      zone: "sort=zone.name,asc&",
    };

    return httpClient
      .sendHttpRequest<Response>({
        endpoint: `/projects/search/rsql?${sortMap[sort]}query=${zoneFilter}${resourceFilter}activityRoot.startDate=isnotnull=true and activityRoot.endDate=isnotnull=true and status.label!=pz_closed and template==false&projection=details&page=${page}&size=${size}`,
        method: HttpMethods.GET,
      })
      .then(({ _embedded }) => (_embedded ? _embedded.projects : []))
      .then<
        (Identifiable<LinkedProject> & { activityRoot: Identifiable<List> })[]
      >((promises) => Promise.all(promises))
      .then((projects) =>
        projects.map((project) => ({
          ...project,
          rootList: { ...list, ...project.activityRoot },
          isTemplate: project.isTemplate || project.template,
          tags: project.tags
            ? (project.tags as any).map((name: string) => ({ name }))
            : [],
        })),
      )
      .then(projectsToGantt);
  };
}

export { getGantt };
